import * as d3 from "d3";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type WheelEvent,
} from "react";

import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { PinnedNodesContext, StateContext } from "@client/pages/round/Round";
import { NodeColors } from "@client/utils/NodeColors";
import { useOnResize } from "@client/utils/useOnResize";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import { assertGetEvaluable } from "@engine/round/accessors";
import { matchLimit, RoundAggregate } from "@engine/round/aggregate";
import type { MVF } from "@lib/tranche";
import { ldtFromMVF, valueFromMVF } from "@lib/tranche/trancheUtils";
import { clamp } from "@lib/utils/clamp";
import { FrontendEvaluation, MVFUpdate } from "../useEvaluatorEvaluationsCache";
import { MVFFunction } from "./MVFFunction";

export type Domains = {
  xDomain: [number, number];
  yDomain: [number, number];
};

const Axis = ({
  axisCreator,
  transform,
}: {
  axisCreator: d3.Axis<d3.NumberValue>;
  transform?: string;
}) => {
  const axisRef = (axis: SVGGElement | null) => {
    if (axis) {
      axisCreator(d3.select(axis));
    }
  };
  return <g style={{ fontSize: "12px" }} ref={axisRef} transform={transform} />;
};

export type D3Axis = d3.ScaleLinear<number, number, never>;

const paddingBottom = 50;
const paddingTop = 30;
const paddingLeft = 130;
const paddingRight = 220;

export function getMaxFdvLdt(
  evaluations: FrontendEvaluation[],
  holdProposalId: string,
): {
  maxFDV: number;
  maxLDT: number;
} {
  let maxFDV = 0;
  let maxLDT = 0;

  for (const evaluation of evaluations) {
    maxFDV = Math.max(maxFDV, evaluation.fdv);
    if (holdProposalId === evaluation.evaluableId) {
      continue;
    }
    maxLDT = Math.max(maxLDT, ldtFromMVF(evaluation.mvf));
  }

  return { maxFDV: maxFDV * 1.1, maxLDT: maxLDT * 1.1 };
}

function isBasicallyZeroMVF(mvf: MVF, epsilon: number = 1e-6): boolean {
  return mvf.every((point) => point.value < epsilon);
}

type SvgTooltipContextType = {
  container: SVGGElement | null;
};
export const SvgTooltipContext =
  React.createContext<SvgTooltipContextType | null>(null);

export const MVFGraph = withErrorBoundary(
  ({
    evaluations,
    userCanEditEvaluations,
    mvfUpdate,
    mvfCommit,
    unilateralAllocations,
    domains: { xDomain, yDomain },
    setDomains,
  }: {
    evaluations: FrontendEvaluation[];
    userCanEditEvaluations: boolean;
    mvfUpdate: MVFUpdate;
    mvfCommit: () => void;
    unilateralAllocations?: {
      [evaluableId: string]: {
        amount: number;
      };
    };
    domains: Domains;
    setDomains: (domains: Domains) => void;
  }) => {
    const { round, actingAsUserId: userId } = useContext(StateContext);
    const { pinnedNodes } = useContext(PinnedNodesContext);

    evaluations = evaluations.filter(
      (evaluation) => !isBasicallyZeroMVF(evaluation.mvf),
    );
    const { maxFDV, maxLDT } = getMaxFdvLdt(evaluations, round.holdProposalId);
    const defaultMaxLdt = Math.max(2_500_000, maxLDT);

    const [dragOrigin, setDragOrigin] = useState<{
      x: number;
      y: number;
      xDomain: [number, number];
      yDomain: [number, number];
    } | null>(null);

    const [dimensions, setDimensions] = useState({
      height: 400,
      width: 600,
    });
    const ref = useRef<HTMLDivElement>(null);

    useOnResize(() => {
      if (ref.current) {
        if (
          ref.current.clientHeight === dimensions.height &&
          ref.current.clientWidth === dimensions.width
        ) {
          return;
        }

        setDimensions({
          height: ref.current.clientHeight,
          width: ref.current.clientWidth,
        });
      }
    });

    const xAxis = d3
      .scaleLinear()
      .domain(xDomain)
      .range([paddingLeft, dimensions.width + paddingLeft - paddingRight]);

    const yAxis = d3
      .scaleLinear()
      .domain(yDomain)
      .range([dimensions.height - paddingBottom, paddingTop]);

    let fillToY: number | null = null;
    if (unilateralAllocations) {
      for (const evaluation of evaluations) {
        const alloc = unilateralAllocations[evaluation.evaluableId];
        if (!alloc || alloc.amount === 0) {
          continue;
        }

        const mv = valueFromMVF(evaluation.mvf, Math.round(alloc.amount));
        fillToY = Math.min(fillToY || mv, mv);
        fillToY = Math.max(fillToY, yDomain[0]);
      }
    }

    useEffect(() => {
      const closeEventListener = () => {
        mvfCommit();
      };
      window.addEventListener("beforeunload", closeEventListener);
      return () =>
        window.removeEventListener("beforeunload", closeEventListener);
    }, [mvfCommit]);

    evaluations.sort((eval1, eval2) => {
      const eval1Pinned = pinnedNodes.includes(eval1.evaluableId);
      const eval2Pinned = pinnedNodes.includes(eval2.evaluableId);
      if (eval1Pinned !== eval2Pinned) {
        return eval1Pinned ? 1 : -1;
      }

      if (eval1.evaluable.shortname === eval2.evaluable.shortname) {
        return 0;
      }

      return eval2.fdv - eval1.fdv;
    });

    function handleWheel(event: WheelEvent<SVGElement>) {
      if (!ref.current) {
        return;
      }

      const rect = ref.current.getBoundingClientRect();
      const scrollSensitivity = 0.2;

      const graphWidth = xAxis(xDomain[1]) - xAxis(xDomain[0]);
      const graphHeight = yAxis(yDomain[0]) - yAxis(yDomain[1]);

      // translate mouse coordinates to graph coordinates
      const graphMouseX = clamp(
        event.clientX - rect.left - xAxis.range()[0],
        0,
        graphWidth,
      );

      const graphMouseY = clamp(
        event.clientY - rect.top - yAxis.range()[1],
        0,
        graphHeight,
      );

      const graphMousePercentX = graphMouseX / graphWidth;
      const graphMousePercentY = graphMouseY / graphHeight;

      // non-linear zoom: zooming should always show/hide the same number of
      // pixels, regardless of the current zoom level
      const scrollRateX =
        (xAxis.invert(event.deltaY) - xAxis.invert(0)) * scrollSensitivity;

      const scrollRateY =
        (yAxis.invert(event.deltaY) - yAxis.invert(0)) * scrollSensitivity * -1;

      const newXDomainStart = Math.max(
        xDomain[0] - scrollRateX * graphMousePercentX,
        0,
      );
      const newXDomainEnd = Math.min(
        xDomain[1] + scrollRateX * (1 - graphMousePercentX),
        defaultMaxLdt,
      );
      const newYDomainStart = Math.max(
        yDomain[0] - scrollRateY * (1 - graphMousePercentY),
        0,
      );
      const newYDomainEnd = Math.min(
        yDomain[1] + scrollRateY * graphMousePercentY,
        Math.max(1, maxFDV),
      );

      setDomains({
        xDomain: [newXDomainStart, newXDomainEnd],
        yDomain: [newYDomainStart, newYDomainEnd],
      });
    }

    function handleMouseLeave() {
      if (dragOrigin) {
        setDragOrigin(null);
      }

      mvfCommit();
    }

    function handleMouseMove(event: React.MouseEvent<SVGElement>) {
      if (!dragOrigin) {
        return;
      }

      let deltaX = xAxis.invert(event.clientX) - xAxis.invert(dragOrigin.x);
      let newXDomainStart = dragOrigin.xDomain[0] - deltaX;
      if (newXDomainStart < 0) {
        deltaX += newXDomainStart;
        newXDomainStart = 0;
      }
      const newXDomainEnd = dragOrigin.xDomain[1] - deltaX;

      let deltaY = yAxis.invert(event.clientY) - yAxis.invert(dragOrigin.y);
      let newYDomainStart = dragOrigin.yDomain[0] - deltaY;
      if (newYDomainStart < 0) {
        deltaY += newYDomainStart;
        newYDomainStart = 0;
      }
      const newYDomainEnd = dragOrigin.yDomain[1] - deltaY;

      setDomains({
        xDomain: [newXDomainStart, newXDomainEnd],
        yDomain: [newYDomainStart, newYDomainEnd],
      });
    }

    function handleMouseDown(event: React.MouseEvent<SVGElement>) {
      setDragOrigin({ x: event.clientX, y: event.clientY, xDomain, yDomain });
    }

    // Create a container for tooltips inside the SVG, so that they are on top of everything in the z-order. See `createPortal` usage in `DraggableCircle` for an example.
    const tooltipContainerRef = useRef<SVGGElement | null>(null);
    const tooltipContext = useMemo(
      () => ({
        container: tooltipContainerRef.current,
      }),
      // TODO(joel): We're warned that tooltipContainerRef.current is an unnecessary dependency. Jason strongly warned me against messing with dependency lists so I'm leaving it for now but we should perhaps investigate.
      [tooltipContainerRef.current], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const AxisBreak = ({ axis }: { axis: "x" | "y" }) => {
      const x = paddingLeft + (axis === "x" ? 5 : -5);
      const y = dimensions.height - paddingBottom - (axis === "x" ? 5 : 25);
      const widthValue = axis === "x" ? 20 : 10;
      const heightValue = axis === "x" ? 12 : 20;

      const transform =
        axis === "y" ? `rotate(90, 0, 10) translate(-10, 0)` : undefined;

      return (
        <svg x={x} y={y} width={widthValue} height={heightValue}>
          <g transform={transform}>
            <rect
              x="0"
              y="0"
              width="20"
              height="12"
              className="fill-[#f5f5f4] dark:fill-[#181818]"
            />
            <g className="dark:stroke-white stroke-black">
              <line x1="0" y1="5" x2="6" y2="0" />
              <line x1="6" y1="0" x2="14" y2="10" />
              <line x1="14" y1="10" x2="20" y2="5" />
            </g>
          </g>
        </svg>
      );
    };

    return (
      <div className="relative flex flex-col select-none flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden" ref={ref}>
          <SvgTooltipContext.Provider value={tooltipContext}>
            <svg
              width="100%"
              height="100%"
              style={{ cursor: dragOrigin ? "move" : "default" }}
              onWheel={handleWheel}
              onMouseLeave={handleMouseLeave}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={() => setDragOrigin(null)}
            >
              <g>
                <text
                  x="50"
                  y="14"
                  fontSize="0.8em"
                  className="dark:fill-white fill-black"
                >
                  First dollar value
                </text>
                <Axis
                  axisCreator={d3.axisLeft(yAxis).ticks(3)}
                  transform={`translate(${paddingLeft},0)`}
                />
                <text
                  x={dimensions.width - 125}
                  y={dimensions.height - paddingBottom + 35}
                  fontSize="0.8em"
                  className="dark:fill-white fill-black"
                >
                  Last dollar threshold
                </text>
                <Axis
                  axisCreator={d3
                    .axisBottom(xAxis)
                    .ticks(4)
                    .tickFormat((x) => "$" + d3.format(".2s")(x))}
                  transform={`translate(0,${dimensions.height - paddingBottom})`}
                />

                {xDomain[0] > 0 && <AxisBreak axis="x" />}
                {yDomain[0] > 0 && <AxisBreak axis="y" />}

                {evaluations.map((evaluation) => (
                  <MVFFunction
                    evaluation={evaluation}
                    key={evaluation.evaluableId + evaluation.evaluatorId}
                    xAxis={xAxis}
                    yAxis={yAxis}
                    mvfUpdate={mvfUpdate}
                    fillToY={fillToY || 0}
                    isEditable={
                      userCanEditEvaluations &&
                      !evaluation.isDeferred &&
                      round.members[userId]?.initials !== "JT"
                    }
                    isHold={round.holdProposalId === evaluation.evaluableId}
                  />
                ))}

                <MVFAmountLabels round={round} xAxis={xAxis} yAxis={yAxis} />

                <g ref={tooltipContainerRef} />
              </g>
            </svg>
          </SvgTooltipContext.Provider>
        </div>
      </div>
    );
  },
);

function MVFAmountLabels({
  round,
  xAxis,
  yAxis,
}: {
  round: RoundAggregate;
  xAxis: D3Axis;
  yAxis: D3Axis;
}) {
  const { hoveredNode, pinnedNodes } = useContext(PinnedNodesContext);
  const { isDarkMode } = useTheme();

  const nodes = [...pinnedNodes];
  if (hoveredNode != null) {
    if (!pinnedNodes.includes(hoveredNode)) {
      nodes.push(hoveredNode);
    }
  }
  const evaluables = nodes.map((node) => assertGetEvaluable(round, node));
  const [yMin, yMax] = yAxis.domain();
  const [xMin, xMax] = xAxis.domain();

  let labelsShown = -1;

  const yPixelStart = yAxis.range()[1];
  const yPixelLimit = yAxis.range()[0];

  return (
    <g>
      {evaluables.map((evaluable) => {
        const initialColor = NodeColors.getColor(round, evaluable, false);

        const color = isDarkMode
          ? initialColor
          : new TinyColor(initialColor).darken(10).toHslString();

        // The match limit is on top of the baseline standard grant amount, so
        // we display it to the right of the baseline standard grant amount.
        let unadjustedMatchLimit: number = 0;
        let adjustedMatchLimit: undefined | number = undefined;
        if (evaluable.type === "proposal") {
          unadjustedMatchLimit = matchLimit(evaluable) ?? 0;
          if (
            unadjustedMatchLimit &&
            evaluable.typedFields.baselineStandardGrantAmount != undefined
          ) {
            adjustedMatchLimit =
              unadjustedMatchLimit +
              evaluable.typedFields.baselineStandardGrantAmount;
          }
        }

        const amountLabelsSource = [
          ...(evaluable.type === "proposal"
            ? [
                {
                  label: "Baseline standard grant",
                  value: evaluable.typedFields.baselineStandardGrantAmount,
                },
                ...(evaluable.requestsMatchingPledge
                  ? [
                      {
                        label: "Match limit",
                        value: adjustedMatchLimit,
                        valueDisplayOverride:
                          d3.format(".2s")(unadjustedMatchLimit),
                      },
                    ]
                  : []),
                {
                  label: "Base request",
                  value: evaluable.typedFields.baseRequestAmount,
                },
                ...(evaluable.typedFields.minGrantAmount &&
                evaluable.typedFields.minGrantAmount >= 1000
                  ? [
                      {
                        label: "Minimum request",
                        value: evaluable.typedFields.minGrantAmount,
                      },
                    ]
                  : []),
                {
                  label: "Ambitious request",
                  value: evaluable.typedFields.maxGrantAmount,
                },
                {
                  label: "Max request",
                  value: evaluable.typedFields.maxGrantAmount,
                },
              ].sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
            : []),
        ].filter((label) => label.value !== undefined) as {
          label: string;
          value: number;
          valueDisplayOverride?: string;
        }[];

        const underflowLabels: typeof amountLabelsSource = [];
        const visibleLabels: typeof amountLabelsSource = [];
        const overflowLabels: typeof amountLabelsSource = [];

        for (const label of amountLabelsSource) {
          if (label.value < xMin) {
            underflowLabels.push(label);
          } else if (label.value < xMax) {
            visibleLabels.push(label);
          } else {
            overflowLabels.push(label);
          }
        }

        return (
          <Fragment key={evaluable.id}>
            {underflowLabels.map((label) => {
              const xPosition = xAxis.range()[0];
              const yPositionIfRendered = yPixelStart + (labelsShown + 1) * 15;
              if (yPositionIfRendered + 5 < yPixelLimit) {
                labelsShown++;
                const currentLabelYPosition = yPixelStart + labelsShown * 15;
                const markerId = `underflow-arrowhead-${labelsShown}`;
                return (
                  <g key={label.label}>
                    <defs>
                      <marker
                        id={markerId}
                        markerWidth="6"
                        markerHeight="6"
                        refX="3"
                        refY="3"
                        orient="auto"
                        markerUnits="strokeWidth"
                      >
                        <polygon points="6,0 0,3 6,6" fill={color} />
                      </marker>
                    </defs>
                    <line
                      x1={xPosition + 13}
                      y1={currentLabelYPosition}
                      x2={xPosition + 35}
                      y2={currentLabelYPosition}
                      stroke={color}
                      strokeWidth={2}
                      markerStart={`url(#${markerId})`}
                      className="pointer-events-none"
                    />
                    <text
                      x={xPosition + 40}
                      y={currentLabelYPosition}
                      fill={color}
                      fontSize="10"
                      alignmentBaseline="middle"
                      textAnchor="start"
                      className="pointer-events-none"
                    >
                      {label.label}: $
                      {label.valueDisplayOverride ??
                        d3.format(".2s")(label.value)}
                    </text>
                  </g>
                );
              }
              return null;
            })}
            {visibleLabels.map((label) => {
              const yPositionIfRendered = yPixelStart + (labelsShown + 1) * 15;
              if (yPositionIfRendered + 7 < yPixelLimit) {
                labelsShown++;
                const currentLabelYPosition = yPixelStart + labelsShown * 15;
                return (
                  <Fragment key={label.label}>
                    <line
                      x1={xAxis(label.value)}
                      y1={yAxis(yMin)}
                      x2={xAxis(label.value)}
                      y2={yAxis(yMax)}
                      stroke={color}
                      strokeWidth={1}
                      strokeDasharray="2,2"
                      className="pointer-events-none"
                    />
                    <text
                      x={xAxis(label.value) + 5}
                      y={currentLabelYPosition}
                      fill={color}
                      fontSize="10"
                      className="pointer-events-none"
                    >
                      {label.label}: $
                      {label.valueDisplayOverride ??
                        d3.format(".2s")(label.value)}
                    </text>
                  </Fragment>
                );
              }
              return null;
            })}
            {overflowLabels.map((label) => {
              const xPosition = xAxis.range()[1];
              const yPositionIfRendered = yPixelStart + (labelsShown + 1) * 15;
              if (yPositionIfRendered + 5 < yPixelLimit) {
                labelsShown++;
                const currentLabelYPosition = yPixelStart + labelsShown * 15;
                const markerId = `overflow-arrowhead-${labelsShown}`;
                return (
                  <g key={label.label}>
                    <line
                      x1={xPosition + 10}
                      y1={currentLabelYPosition}
                      x2={xPosition + 32}
                      y2={currentLabelYPosition}
                      stroke={color}
                      strokeWidth={2}
                      markerEnd={`url(#${markerId})`}
                      className="pointer-events-none"
                    />
                    <defs>
                      <marker
                        id={markerId}
                        markerWidth="6"
                        markerHeight="6"
                        refX="3"
                        refY="3"
                        orient="auto"
                        markerUnits="strokeWidth"
                      >
                        <polygon points="0,0 6,3 0,6" fill={color} />
                      </marker>
                    </defs>
                    <text
                      x={xPosition}
                      y={currentLabelYPosition}
                      fill={color}
                      fontSize="10"
                      alignmentBaseline="middle"
                      className="pointer-events-none"
                      textAnchor="end"
                    >
                      {label.label}: $
                      {label.valueDisplayOverride ??
                        d3.format(".2s")(label.value)}
                    </text>
                  </g>
                );
              }
              return null;
            })}
          </Fragment>
        );
      })}
    </g>
  );
}
